/* ==========================================================================
   <ef-rail> — horizontal product rail with arrow controls

   The rail is a native overflow scroller, so it works with no JS at all:
   trackpad, touch swipe and shift-wheel already scroll it, and the track
   carries tabindex="0" so a keyboard can reach it. This file only adds the
   arrows on top of that, and removes them again when everything already fits.

   Arrows step by the visible width minus one item, so the card that was at the
   edge stays on screen and the reader keeps their place — a full-width jump
   makes it feel like content was skipped.
   ========================================================================== */

(function () {
  'use strict';

  if (!('customElements' in window)) return;

  customElements.define(
    'ef-rail',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ef-rail-track]');
        this.prevBtn = this.querySelector('[data-ef-rail-prev]');
        this.nextBtn = this.querySelector('[data-ef-rail-next]');
        if (!this.track) return;

        // In RTL, scrollLeft runs negative — so "next" is a negative delta.
        // NOT `this.dir`: that is HTMLElement's own text-direction property. It
        // coerces whatever you assign to a string and reflects it as a dir=""
        // attribute, so `this.dir = 1` reads back as "" and silently multiplies
        // every scroll distance to zero. The arrows then no-op with no error.
        this.axis = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

        this.onScroll = this.throttle(() => this.update());
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

        // Card widths are a percentage of the track, so every resize changes
        // both the step distance and whether the arrows are needed at all.
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(this.throttle(() => this.update()));
          this.ro.observe(this.track);
        } else {
          this.onResize = this.throttle(() => this.update());
          window.addEventListener('resize', this.onResize);
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.ro) this.ro.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        cancelAnimationFrame(this.raf);
        clearTimeout(this.settleTimer);
      }

      throttle(fn) {
        var frame = null;
        return function () {
          if (frame) return;
          frame = requestAnimationFrame(function () {
            frame = null;
            fn();
          });
        };
      }

      step(direction) {
        var item = this.track.querySelector('[data-ef-rail-item]');
        var itemWidth = item ? item.getBoundingClientRect().width : 240;
        var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;

        // One screenful less one card, so nothing is jumped over. Never less
        // than a single card, or a narrow phone would barely move.
        var distance = Math.max(this.track.clientWidth - (itemWidth + gap), itemWidth + gap);

        var max = this.track.scrollWidth - this.track.clientWidth;
        var raw = this.track.scrollLeft + distance * direction * this.axis;

        // scrollLeft is negative in RTL, so the range flips with it.
        var target = this.axis === 1
          ? Math.max(0, Math.min(raw, max))
          : Math.min(0, Math.max(raw, -max));

        if (this.track.scrollLeft === target) return;

        if (this.reducedMotion()) {
          this.track.scrollLeft = target;
        } else {
          this.animateTo(target);
        }
      }

      /* Eased by hand, on rAF, rather than scrollTo({behavior:'smooth'}).

         The native call is reported as supported in environments that never
         run the animation, and there it does not merely fail to ease — it
         leaves a pending scroll that pins the position, so the arrows stop
         working entirely and no timeout fallback can rescue them. Assigning
         scrollLeft frame by frame uses only primitives that can be observed
         working, and degrades to an instant jump if a frame is dropped. */
      animateTo(target) {
        var self = this;
        var start = this.track.scrollLeft;
        var delta = target - start;
        var duration = 320;
        var t0 = null;

        cancelAnimationFrame(this.raf);
        clearTimeout(this.settleTimer);

        this.raf = requestAnimationFrame(function frame(now) {
          if (t0 === null) t0 = now;
          var p = Math.min((now - t0) / duration, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          self.track.scrollLeft = start + delta * eased;
          if (p < 1) self.raf = requestAnimationFrame(frame);
        });

        // Frames only arrive while the page is actually rendering. If they stop
        // — a background tab, a throttled or headless view — the tween freezes
        // wherever it got to and the arrow looks broken. Timers keep running in
        // those conditions, so this lands the rail regardless. When the tween
        // does finish normally it is already at `target` and this is a no-op.
        this.settleTimer = setTimeout(function () {
          if (Math.abs(self.track.scrollLeft - target) > 1) {
            cancelAnimationFrame(self.raf);
            self.track.scrollLeft = target;
          }
        }, duration + 80);
      }

      reducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      update() {
        var track = this.track;
        // Sub-pixel layout means scrollLeft rarely hits the exact maximum, so
        // the end test needs slack or "next" never disables.
        var slack = 2;
        var max = track.scrollWidth - track.clientWidth;
        var overflowing = max > slack;

        this.toggleAttribute('data-overflowing', overflowing);

        var pos = Math.abs(track.scrollLeft);
        if (this.prevBtn) this.prevBtn.disabled = !overflowing || pos <= slack;
        if (this.nextBtn) this.nextBtn.disabled = !overflowing || pos >= max - slack;

        // A scroll container is only a keyboard trap worth stopping at when it
        // actually scrolls.
        if (overflowing) {
          track.setAttribute('tabindex', '0');
        } else {
          track.removeAttribute('tabindex');
        }
      }
    }
  );
})();
